import { getSupabaseAdmin, createClient } from '@/lib/supabase/server';
import { NextRequest, NextResponse } from 'next/server';
import { logger } from '@/lib/logger';
import { checkRateLimit, getClientIP, createRateLimitResponse } from '@/lib/rate-limit';
import { createPaymentIntent } from '@/lib/stripe/server';
import { withCSRFProtection } from '@/lib/csrf';
import { 
  PAYMENT_AMOUNT, 
  RATE_LIMIT_REQUESTS_PER_USER as PAYMENT_RATE_LIMIT,
  RATE_LIMIT_WINDOW_MS as PAYMENT_WINDOW_MS,
  HTTP_STATUS_INTERNAL_SERVER_ERROR 
} from '@/lib/constants';

const handler = async (request: NextRequest) => {
  try {
    // --- Rate limiting (by IP as initial check) ---
    const clientIP = getClientIP(request);
    const ipRateLimit = checkRateLimit(`payment-ip:${clientIP}`, PAYMENT_RATE_LIMIT, PAYMENT_WINDOW_MS);
    
    if (!ipRateLimit.allowed) {
      const retryAfter = Math.ceil((ipRateLimit.resetTime - Date.now()) / 1000);
      return createRateLimitResponse(clientIP, retryAfter);
    }
    
    const supabase = await createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    
    if (!user) {
      return NextResponse.json({ error: 'Unauthorized' }, { status: 401 });
    }
    
    // --- Stricter rate limiting by user ID ---
    const userRateLimit = checkRateLimit(`payment-user:${user.id}`, PAYMENT_RATE_LIMIT, PAYMENT_WINDOW_MS);
    
    if (!userRateLimit.allowed) {
      const retryAfter = Math.ceil((userRateLimit.resetTime - Date.now()) / 1000);
      return createRateLimitResponse(user.id, retryAfter);
    }

    const body = await request.json();
    const { match_id: matchId, company_id: companyId } = body;
    const amount = PAYMENT_AMOUNT; // Hardcoded amount to prevent client override

    if (!matchId || !companyId) {
      return NextResponse.json({ error: 'Missing required fields' }, { status: 400 });
    }

    // Verify user owns the company
    const { data: company } = await supabase
      .from('companies')
      .select('id, name')
      .eq('id', companyId)
      .eq('profile_id', user.id)
      .single();

    if (!company) {
      return NextResponse.json({ error: 'Company not found or unauthorized' }, { status: 403 });
    }

    // Verify match exists and belongs to company's job (can be in any status except rejected)
    const { data: match } = await supabase
      .from('matches')
      .select('status, jobs!inner(company_id, title)')
      .eq('id', matchId)
      .neq('status', 'rejected')
      .eq('jobs.company_id', companyId)
      .single();

    if (!match) {
      return NextResponse.json({ error: 'Match not found, already rejected, or unauthorized' }, { status: 403 });
    }

    // Check if payment already exists and is charged (prevent duplicate charges)
    const { data: existingPayment } = await supabase
      .from('payments')
      .select('id, status')
      .eq('match_id', matchId)
      .eq('status', 'charged')
      .maybeSingle();

    if (existingPayment) {
      return NextResponse.json({ error: 'Payment already completed for this hire' }, { status: 400 });
    }

    // Create Stripe PaymentIntent
    const jobTitle = (match.jobs as { title?: string }).title || 'Job';
    const { clientSecret, paymentIntentId } = await createPaymentIntent({
      amount,
      matchId: matchId,
      companyId: companyId,
      companyName: company.name,
      description: `Hire for ${jobTitle}`,
    });

    // Atomic upsert: insert or update if conflict on match_id
    // This prevents race conditions from concurrent payment creation requests
    const supabaseService = getSupabaseAdmin();

    const { data: payment, error } = await supabaseService
      .from('payments')
      .upsert(
        {
          match_id: matchId,
          company_id: companyId,
          amount,
          status: 'pending',
          stripe_payment_id: paymentIntentId,
        },
        {
          onConflict: 'match_id',
          ignoreDuplicates: false,
        }
      )
      .select("id, match_id, company_id, amount, status, stripe_payment_id, created_at")
      .single();

    if (error) {
      return NextResponse.json({ error: error.message }, { status: HTTP_STATUS_INTERNAL_SERVER_ERROR });
    }

    return NextResponse.json({ 
      payment,
      clientSecret,
      publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    });
  } catch (error) {
    logger.error('Payment creation error:', error);
    if (error instanceof Error && error.message.includes('Stripe')) {
      return NextResponse.json({ error: 'Payment service unavailable' }, { status: 503 });
    }
    return NextResponse.json({ error: 'Internal server error' }, { status: HTTP_STATUS_INTERNAL_SERVER_ERROR });
  }
}

// Apply CSRF protection to the handler
export const POST = withCSRFProtection(handler);
